import { useCallback, useEffect, useState } from 'react'
import useSWR, { mutate } from 'swr'
import type { PaginationInfo } from 'types/common'
import { generateDefaultQueryParams } from 'types/default-params'
import type {
  BulkUserActionRequest,
  CreateUserDTO,
  UpdateUserDTO,
  User,
  UserQueryParams,
  UserSearch,
  UserWithPlanInfo,
} from 'types/user'
import type { UserProfile } from 'types/user-profile'
import { deleteWithTokenWithStatus, fetchWithToken, postWithToken, putWithToken } from './http'

export function useFetchOrganizationUsers(token: string, orgId: string, params: [[string, string]] | null) {
  const { data, error, isLoading } = useSWR([`organizations/${orgId}/users`, token, params], ([url, token, params]) =>
    fetchWithToken(url, token, params)
  )

  return {
    users: data?.users as User[],
    isLoading,
    isError: error,
  }
}

export function useFetchUser(
  token: string,
  userId: string,
  params: [[string, string]] | null,
  shouldFetch: boolean = true
) {
  const [isLoading, setIsLoading] = useState(false)
  const { data, error, mutate } = useSWR(
    shouldFetch && userId ? [`users/${userId}`, token, params] : null,
    ([url, token, params]) => fetchWithToken(url, token, params),
    {
      suspense: false,
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
    }
  )

  useEffect(() => {
    if (shouldFetch && userId) {
      setIsLoading(true)
      mutate().finally(() => {
        setIsLoading(false)
      })
    }
  }, [userId, shouldFetch, mutate])

  return {
    userProfile: data as UserProfile | undefined,
    isLoading,
    isError: error,
    mutate,
  }
}

function removeNullProperties(obj: UserSearch): UserSearch {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      if (value === null) return false
      if (key === 'searchTerm' && value === '') return false
      if (key === 'organizationIds' && Array.isArray(value) && value.length === 0) return false
      return true
    })
  )
}

export function useSearchUsers(token: string, params: UserSearch, shouldFetch: boolean = true) {
  const filteredParams = removeNullProperties(params)

  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`users/search`, token, JSON.stringify(filteredParams)] : null,
    ([url, token, params]) => postWithToken(url, token, JSON.parse(params))
  )

  return {
    users: data?.users as User[],
    pagination: data?.paginationInfo as PaginationInfo,
    isLoading,
    isError: error,
  }
}

export function useSearchUsersWithPlanInfo(token: string, queryParams: UserQueryParams, shouldFetch: boolean = true) {
  const params = generateDefaultQueryParams(queryParams)
  params.push(['organizationIds', queryParams.organizationIds.join(',')])
  params.push(['includeWithoutPlans', queryParams.includeWithoutPlans ? 'true' : 'false'])
  params.push(['includeUnapproved', queryParams.includeUnapproved ? 'true' : 'false'])
  params.push(['includeApproved', queryParams.includeApproved ? 'true' : 'false'])
  params.push(['includeCurrentMembers', queryParams.includeCurrentMembers ? 'true' : 'false'])
  params.push(['includeFormerMembers', queryParams.includeFormerMembers ? 'true' : 'false'])
  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? [`users/search-with-plan-info`, token, params] : null,
    ([url, token, params]) => fetchWithToken(url, token, params),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      revalidateIfStale: true,
      dedupingInterval: 2000, // Reduce deduping interval for more responsive updates
    }
  )

  return {
    users: data?.users as UserWithPlanInfo[],
    pagination: data?.paginationInfo as PaginationInfo,
    isLoading,
    isError: error,
    mutate,
  }
}

interface UseCreateUserReturn {
  createUser: (userDTO: CreateUserDTO) => Promise<User>
  isLoading: boolean
  error: Error | null
  createdUser: User | null
}

export function useCreateUser(token: string): UseCreateUserReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [createdUser, setCreatedUser] = useState<User | null>(null)

  const createUser = useCallback(
    (userDTO: CreateUserDTO): Promise<User> => {
      setIsLoading(true)
      setError(null)
      setCreatedUser(null)

      return postWithToken('users', token, userDTO)
        .then((response) => {
          setCreatedUser(response as User)
          // Invalidate user list caches to show the new user
          mutate(
            (key) => {
              if (typeof key === 'string') {
                return key.startsWith('users')
              }
              if (Array.isArray(key)) {
                const endpoint = key[0]
                return (
                  typeof endpoint === 'string' &&
                  (endpoint.includes('users/search') || endpoint.includes('users/search-with-plan-info'))
                )
              }
              return false
            },
            undefined,
            true
          )
          return response as User
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return {
    createUser,
    isLoading,
    error,
    createdUser,
  }
}

interface UseUpdateUserReturn {
  updateUser: (userId: string, userDTO: UpdateUserDTO) => Promise<User>
  isLoading: boolean
  error: Error | null
  updatedUser: User | null
}

export function useUpdateUser(token: string): UseUpdateUserReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [updatedUser, setUpdatedUser] = useState<User | null>(null)

  const updateUser = useCallback(
    (userId: string, userDTO: UpdateUserDTO): Promise<User> => {
      setIsLoading(true)
      setError(null)
      setUpdatedUser(null)

      return putWithToken(`users/${userId}`, token, userDTO)
        .then((response) => {
          setUpdatedUser(response as User)
          // Invalidate the cache for this user and any relevant user lists
          mutate(
            (key) => {
              if (typeof key === 'string') {
                return key.includes(`users/${userId}`) || key.startsWith('users')
              }
              if (Array.isArray(key)) {
                const endpoint = key[0]
                return (
                  typeof endpoint === 'string' &&
                  (endpoint.includes(`users/${userId}`) ||
                    endpoint.includes('users/search') ||
                    endpoint.includes('users/search-with-plan-info'))
                )
              }
              return false
            },
            undefined,
            true
          )
          return response as User
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return {
    updateUser,
    isLoading,
    error,
    updatedUser,
  }
}

export function useDeleteUserFromOrganization(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deleteUser = async (userId: string, organizationId: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const { status, data } = await deleteWithTokenWithStatus(`organizations/${organizationId}/users/${userId}`, token)
      if (status === 200) {
        // Invalidate user list caches to remove the deleted user
        mutate(
          (key) => {
            if (typeof key === 'string') {
              return key.includes(`users/${userId}`) || key.startsWith('users')
            }
            if (Array.isArray(key)) {
              const endpoint = key[0]
              return (
                typeof endpoint === 'string' &&
                (endpoint.includes(`users/${userId}`) ||
                  endpoint.includes('users/search') ||
                  endpoint.includes('users/search-with-plan-info'))
              )
            }
            return false
          },
          undefined,
          true
        )
      }
      return { success: status === 200, status, data }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete user'))
      return { success: false, status: 500, data: null }
    } finally {
      setIsLoading(false)
    }
  }

  return { deleteUser, isLoading, error }
}

export function useRequireUserResetPassword(token: string) {
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const requireUserResetPassword = async (userId: string) => {
    setIsSending(true)
    setError(null)
    try {
      const response = await postWithToken(`users/${userId}/require-reset`, token, {})
      return { success: true, data: response }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to require user password reset')
      setError(error)
      return { success: false, data: null }
    } finally {
      setIsSending(false)
    }
  }

  return { requireUserResetPassword, isSending, error }
}

export function useSendUserChangePassword(token: string) {
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const sendUserChangePassword = async (userId: string) => {
    setIsSending(true)
    setError(null)
    try {
      const response = await postWithToken(`users/${userId}/send-change-password`, token, {})
      return { success: true, data: response }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to change user password')
      setError(error)
      return { success: false, data: null }
    } finally {
      setIsSending(false)
    }
  }

  return { sendUserChangePassword, isSending, error }
}

export function useBulkRequireUserResetPassword(token: string) {
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const bulkUserResetPassword = async (request: BulkUserActionRequest) => {
    setIsSending(true)
    setError(null)
    try {
      const response = await postWithToken(`users/require-reset`, token, request)
      return { success: true, data: response }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to require user password reset')
      setError(error)
      return { success: false, data: null }
    } finally {
      setIsSending(false)
    }
  }

  return { bulkUserResetPassword, isSending, error }
}

export function useSendUserInvite(token: string) {
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const sendUserInvite = async (userId: string) => {
    setIsSending(true)
    setError(null)
    try {
      const response = await postWithToken(`users/${userId}/invite`, token, {})
      return { success: true, data: response }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to require user password reset')
      setError(error)
      return { success: false, data: null }
    } finally {
      setIsSending(false)
    }
  }

  return { sendUserInvite, isSending, error }
}

export function useBulkSendUserInvite(token: string) {
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const bulkSendUserInvite = async (request: BulkUserActionRequest) => {
    setIsSending(true)
    setError(null)
    try {
      const response = await postWithToken(`users/invite`, token, request)
      return { success: true, data: response }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to require user password reset')
      setError(error)
      return { success: false, data: null }
    } finally {
      setIsSending(false)
    }
  }

  return { bulkSendUserInvite, isSending, error }
}

export function useSendUserPlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const sendUserPlan = async (targetUserId: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await postWithToken(`compensation/plans/user/${targetUserId}/latest/share`, token, {})
      return { success: true, data: response }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to send user plan')
      setError(error)
      return { success: false, data: null }
    } finally {
      setIsLoading(false)
    }
  }

  return { sendUserPlan, isLoading, error }
}
